// react and mui stuff
import { useContext, useEffect, useState } from "react"
import Markdown from "markdown-to-jsx"
import { UserContext } from "../utils/contexts"
import { ToastContext } from "../components/toast"

import Delete from "@mui/icons-material/Delete"
import Edit from "@mui/icons-material/Edit"
import Send from "@mui/icons-material/Send"

import Avatar from "@mui/joy/Avatar"
import Typography from "@mui/joy/Typography"
import IconButton from "@mui/joy/IconButton"
import Modal from "@mui/joy/Modal"
import ModalDialog from "@mui/joy/ModalDialog"
import ModalClose from "@mui/joy/ModalClose"
import DialogTitle from "@mui/joy/DialogTitle"
import DialogContent from "@mui/joy/DialogContent"
import DialogActions from "@mui/joy/DialogActions"
import Button from "@mui/joy/Button"
import Autocomplete from "@mui/joy/Autocomplete"
import Textarea from "@mui/joy/Textarea"
import FormControl from "@mui/joy/FormControl"
import FormLabel from "@mui/joy/FormLabel"

// custom components
import { MetaProps } from "../components/metaprops"

// data and stuff
import { setData } from "../utils/setdata"
import { chatterboxAPI } from "../utils/backend"
import { Channel, Webhook } from "../types/chatterbox"
import { Card } from "@mui/joy"

type Message = {
  messageId: string;
  content: string;
  channelId: string;
  channelName: string;
  speakerId: string;
}

export default function AdminPage() {
  const [isTyping, setIsTyping] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [messageContent, setMessageContent] = useState("")
  const [editContent, setEditContent] = useState("")
  const [editMessage, setEditMessage] = useState<Message>()
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState<Channel[]>([])
  const [activeChannel, setActiveChannel] = useState<Channel|null>(null)
  const [speakers, setSpeakers] = useState<Webhook[]>([])
  const [speaker, setSpeaker] = useState<Webhook|null>(null)
  const { setToast } = useContext(ToastContext)
  const { user, setUser } = useContext(UserContext)

  const icarusID = "icarus"

  useEffect(() => {
    // get data
    if (!user) return;

    chatterboxAPI.getSendables().then(info => {
      if (!setData(info, setToast, user, setUser)) return;
      setLoading(false);

      if (info.channels) {
        info.webhooks.push({ id: icarusID, name: "Icarus" });
        setChannels(info.channels)
      }

      setSpeakers(info.webhooks);
    })

  }, [setToast, setUser, user])

  const handleSend = () => {
    if (loading) return setToast({ text: "Hang on a minute!", color: "warning" });
    if (!messageContent) return setToast({ text: "You need say something!", color: "warning" });

    if (!speaker) return setToast({ text: "You need to select a speaker!", color: "warning" });
    if (speaker.id === icarusID && !activeChannel) return setToast({ text: "You need to select a channel!", color: "warning" });

    const content = messageContent.toString();
    setMessageContent("");
    setLoading(true);

    chatterboxAPI.chatter({
      channelId: activeChannel?.id,
      content
    }, speaker.id).then(info => {
      if (!setData(info, setToast, user, setUser, () => setLoading(false))) return;
      setLoading(false);

      // ooh data!
      const msg = {
        messageId: info.messageId,
        content,
        channelId: activeChannel?.id || speaker.id,
        channelName: activeChannel?.name || speaker.name,
        speakerId: speaker.id
      };

      setMessages([msg, ...messages]);
      setLoading(false);
      setIsTyping(false);
    });
  }

  const handleDelete = (msg: Message) => {
    if (loading) return setToast({ text: "Hang on a minute!", color: "warning" })
    setLoading(true)

    chatterboxAPI.chatter({
      channelId: msg.channelId,
      messageId: msg.messageId,
      deleteMsg: true
    }, msg.speakerId).then(info => {
      if (!setData(info, setToast, user, setUser, () => setLoading(false))) return;

      // ooh data!
      setMessages([...messages.filter(m => !(m.channelId === msg.channelId && m.messageId === msg.messageId))])
      setLoading(false)
    })
  }

  const handleEdit = () => {
    if (loading) return setToast({ text: "Hang on a minute!", color: "warning" });
    if (!editContent) return setToast({ text: "You need say something!", color: "warning" });
    if (!editMessage) return setToast({ text: "You need to select a message to edit!", color: "warning" });

    setLoading(true)

    chatterboxAPI.chatter({
      channelId: editMessage.channelId,
      messageId: editMessage.messageId,
      content: editContent
    }, editMessage.speakerId).then(info => {
      if (!setData(info, setToast, user, setUser, () => setLoading(false))) return;

      const msgs = [...messages];
      const index = msgs.findIndex(m => m.channelId === editMessage.channelId && m.messageId === editMessage.messageId);
      if (index !== -1) msgs[index].content = editContent;

      setMessages(msgs);
      resetEdit();
      setLoading(false);
    })
  }

  if (!user) return <></>

  const resetEdit = () => {
    setEditContent("");
    setEditMessage(undefined)
  };

  return (
    <div className="contentRoot">
      <MetaProps meta={{
        splash: "pirate",
        title: "Chatterbox",
        url: "chatterbox",
        description: "Make the bot say stuff. Unhinged things even."
      }}
      />
      {/** Edit Modal */}
      <Modal open={!!editMessage}>
        <ModalDialog layout="center">
          <ModalClose/>
          <DialogTitle>Edit Message</DialogTitle>
          <DialogContent>
            <Typography fontStyle="italic" level="body-sm">{editMessage?.channelName}</Typography>

            <div style={{ display: "flex", alignItems: "center" }}>
              <Avatar src={user.avatar} sx={{ marginRight: "5px" }}/>
              <div>
                <Typography className="noTextOverflow">{user.displayName}</Typography>
                <Typography>{editMessage?.content}</Typography>
              </div>
            </div>
            <Textarea
              disabled={loading}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Edit Chatter"
            />
          </DialogContent>
          <DialogActions>
          <Button variant="solid" color="danger" onClick={() => handleEdit()}>
              Edit
            </Button>
            <Button variant="plain" color="neutral" onClick={() => resetEdit()}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
      <h1 className="pageTitle">Chatterbox Console</h1>
      <br/>
        <Card>
          {/** Speaker Selector */}
          <FormControl>
            <FormLabel>Speaker Selector</FormLabel>
            <Autocomplete
              getOptionKey={c => c.id}
              getOptionLabel={c => c.name}
              options={speakers}
              onChange={(e, v) => {
                setSpeaker(v)
                if (v?.id !== icarusID) setActiveChannel(null);
              }}
              value={speaker}
              required={true}
              disabled={loading}
              placeholder="Select a speaker"
            />
          </FormControl>
          {/** Channel Selector */}
          {
            speaker?.id === icarusID &&
            <FormControl>
              <FormLabel>Channel Selector</FormLabel>
              <Autocomplete
                getOptionKey={c => c.id}
                getOptionLabel={c => c.name}
                options={channels}
                onChange={(e, v) => setActiveChannel(v)}
                value={activeChannel}
                disabled={loading}
                placeholder="Select a channel"
              />
            </FormControl>
          }
          {/** Message history display */}
          <div style={{ height: "500px", overflowY: "auto", display: "flex", flexDirection: "column-reverse", width: "100%" }}>
            {messages.length === 0 && <>No messages yet.</>}
            {messages.length > 0 && <>
              {
                messages.map(m => (
                  <div key={m.messageId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px", borderBottom: "1px black" }}>
                    <div>
                      {/** Channel */}
                      <Typography fontStyle="italic" level="body-sm">{m.channelName}</Typography>
                      {/** Message */}
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Avatar src={user.avatar} sx={{ marginRight: "5px" }}/>
                        <div>
                          <Typography className="noTextOverflow">{user.displayName}</Typography>
                          <Typography><Markdown>{m.content}</Markdown></Typography>
                        </div>
                      </div>
                    </div>
                    {/** Message Actions */}
                    <div>
                      <IconButton aria-label="Edit Message" onClick={() => {
                        setEditMessage(m)
                        setEditContent(m.content)
                      }}><Edit/></IconButton>
                      <IconButton onClick={() => handleDelete(m)} aria-label="Delete Message"><Delete/></IconButton>
                    </div>
                  </div>
                ))
              }
            </>}
          </div>
          {/** Message bar */}
          <div style={{ display: "flex" }}>
            <Textarea
              disabled={!speaker || (speaker.id === icarusID && !activeChannel)}
              value={messageContent}
              minRows={1}
              maxRows={4}
              sx={{ width: "100%" }}
              placeholder="Chatterbox Input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && messageContent.length > 0) handleSend()
              }}
              onChange={(e) => {
                if (e.target.value === "\n") return;
                if (!isTyping && speaker?.id === icarusID) {
                  setIsTyping(true);
                  chatterboxAPI.indicator(activeChannel?.id ?? "");
                  setTimeout(() => {
                    setIsTyping(false);
                  }, 9_000);
                }
                setMessageContent(e.target.value);
              }}
            />
            <IconButton aria-label="Send Message" onClick={handleSend} disabled={messageContent.length === 0}>
              <Send/>
            </IconButton>
          </div>
        </Card>
    </div>
  )
}